import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Plus, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChecklistSelectorModal = ({ open, onClose, projectId, projectName }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [existingChecklists, setExistingChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && projectId) {
      loadData();
    }
  }, [open, projectId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, checklistsRes] = await Promise.all([
        axios.get(`${API}/checklists/templates`),
        axios.get(`${API}/checklists?project_id=${projectId}`)
      ]);
      
      setTemplates(templatesRes.data || []);
      setExistingChecklists(checklistsRes.data || []);
    } catch (error) {
      console.error('Failed to load checklist data:', error);
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async (template) => {
    try {
      const newChecklist = {
        project_id: projectId,
        project_name: projectName,
        template_id: template.id,
        checklist_name: `${template.name} - ${projectName}`,
        checklist_type: template.checklist_type,
        category: template.category,
        status: 'in_progress',
        items: template.items || []
      };

      const response = await axios.post(`${API}/checklists`, newChecklist);
      toast.success('Checklist created from template');
      
      // Navigate to the new checklist detail page
      navigate(`/tools/checklists/${response.data.id}`);
      onClose();
    } catch (error) {
      toast.error('Failed to create checklist');
    }
  };

  const handleOpenExisting = (checklistId) => {
    navigate(`/tools/checklists/${checklistId}`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900">Checklists for {projectName}</DialogTitle>
          <DialogDescription className="text-gray-700 font-medium">
            Select an existing checklist or create a new one from a template
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-gray-700 font-semibold">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Existing Checklists */}
            {existingChecklists.length > 0 && (
              <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-900">
                  <ClipboardList size={18} />
                  Existing Checklists ({existingChecklists.length})
                </h3>
                <div className="space-y-2">
                  {existingChecklists.map((checklist) => (
                    <div
                      key={checklist.id}
                      className="flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                      onClick={() => handleOpenExisting(checklist.id)}
                    >
                      <div className="flex-1">
                        <h4 className="font-bold text-sm text-gray-900">{checklist.checklist_name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs border-gray-400 text-gray-900 font-semibold">
                            {checklist.checklist_type || 'Checklist'}
                          </Badge>
                          <Badge
                            className={`text-xs font-bold ${
                              checklist.status === 'completed'
                                ? 'bg-green-200 text-green-900'
                                : checklist.status === 'in_progress'
                                ? 'bg-blue-200 text-blue-900'
                                : 'bg-gray-300 text-gray-900'
                            }`}
                          >
                            {checklist.status}
                          </Badge>
                          {checklist.completion_percentage !== undefined && (
                            <span className="text-xs text-gray-900 font-bold">
                              {checklist.completion_percentage}% complete
                            </span>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-gray-900">
                        <ArrowRight size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Templates */}
            <div>
              <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-900">
                <Plus size={18} />
                Create from Template
              </h3>
              {templates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="border-2 border-gray-300 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <h4 className="font-bold text-sm mb-2 text-gray-900">{template.name}</h4>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className="text-xs border-gray-400 text-gray-900 font-semibold">
                          {template.checklist_type || 'Checklist'}
                        </Badge>
                        {template.category && (
                          <Badge variant="secondary" className="text-xs bg-gray-300 text-gray-900 font-semibold">
                            {template.category}
                          </Badge>
                        )}
                        {template.items && (
                          <span className="text-xs text-gray-900 font-bold">
                            {template.items.length} items
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleCreateFromTemplate(template)}
                        className="w-full font-mono text-xs font-bold"
                      >
                        <Plus size={14} className="mr-1" />
                        Create from Template
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-700 font-semibold text-center py-8">
                  No templates available. Create templates in the Checklists section.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistSelectorModal;
